// FeatheredImage — Homepage/Speaking-style edge feather implemented with an
// edge-only CSS mask on the <img> itself (NO overlay, NO gradient layer over
// the photo). The masked edge becomes transparent so the section's SOLID
// background shows through, while the photo keeps full color, contrast and
// exposure. Model: the single-edge inner feather used in home/GratitudeRoom.jsx
// and home/WaysToWork.jsx — but without their wash-out overlay div.
//
// The parent controls sizing/position (e.g. absolute inset-0 half-band).

const DIRECTION = {
  left: "to right",
  right: "to left",
  top: "to bottom",
  bottom: "to top",
};

// One edge → an alpha gradient that is transparent at the edge and fully
// opaque by `size` (a % of the image on that axis). Works as an alpha mask in
// both standard and -webkit- implementations.
// The transition distance is tightened to ~60% of `size` so the feather stays
// an edge treatment and does not fog into the composition.
function edgeGradient(side, size) {
  const distance = size * 0.6;
  const mid = Math.round(distance * 0.6);
  const end = Math.round(distance);
  return `linear-gradient(${DIRECTION[side]}, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) ${mid}%, rgba(0,0,0,1) ${end}%)`;
}

export function FeatheredImage({
  src,
  alt = "",
  // Feather span per edge, as a percentage of the image on that axis.
  // Provide only the edges you want to fade (usually just the inner edge).
  featherLeft,
  featherRight,
  featherTop,
  featherBottom,
  objectPosition = "center",
  loading = "lazy",
  className = "",
  style = {},
  testId,
}) {
  const layers = [];
  if (featherLeft != null) layers.push(edgeGradient("left", featherLeft));
  if (featherRight != null) layers.push(edgeGradient("right", featherRight));
  if (featherTop != null) layers.push(edgeGradient("top", featherTop));
  if (featherBottom != null) layers.push(edgeGradient("bottom", featherBottom));

  const maskStyle = layers.length
    ? {
        maskImage: layers.join(", "),
        WebkitMaskImage: layers.join(", "),
        maskRepeat: "no-repeat",
        WebkitMaskRepeat: "no-repeat",
        maskSize: "100% 100%",
        WebkitMaskSize: "100% 100%",
        // Intersect so each specified edge feathers when more than one is set.
        maskComposite: "intersect",
        WebkitMaskComposite: "source-in",
      }
    : {};

  return (
    <img
      src={src}
      alt={alt}
      loading={loading}
      data-testid={testId}
      className={`w-full h-full object-cover ${className}`}
      style={{ objectPosition, ...maskStyle, ...style }}
    />
  );
}

export default FeatheredImage;
